import React, { useState, useEffect, useCallback } from 'react';
import {
    Container,
    Typography,
    Box,
    Grid,
    Card,
    CardContent,
    CircularProgress,
    Alert,
    Chip,
    IconButton,
    Collapse,
    Button,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    SelectChangeEvent,
    Breadcrumbs,
    Link as MuiLink,
} from '@mui/material';
import { Link as RouterLink, useParams } from 'react-router-dom';
import RefreshIcon from '@mui/icons-material/Refresh';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import ErrorIcon from '@mui/icons-material/Error';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import DnsIcon from '@mui/icons-material/Dns';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { LineChart } from '@mui/x-charts';
import { PieChart } from '@mui/x-charts';
import { getServersHealth, getServerHealthCharts } from '../Api';
import ServerHealthStatusResponseDto, { ServerHealthMetricStatusResponse, ServerHealthStatus } from '../models/dtos/ServerHealthStatusResponseDto';
import ServerHealthChartResponseDto, { ServerHealthChartTimeFilter, ChartRecord } from '../models/dtos/ServerHealthChartResponseDto';

// ─── Color constants ─────────────────────────────────────────────────────────

const COLOR_GREEN = '#4caf50';
const COLOR_YELLOW = '#ffeb3b';
const COLOR_ORANGE = '#ff9800';
const COLOR_RED = '#f44336';
const COLOR_GRAY = '#9e9e9e';
const COLOR_UNUSED_GRAY = '#e0e0e0';

// ─── Status helpers ──────────────────────────────────────────────────────────

const getServerStatusHex = (status: ServerHealthStatus): string => {
    switch (status) {
        case 'HEALTHY': return COLOR_GREEN;
        case 'UNDER_PRESSURE': return COLOR_YELLOW;
        case 'SICK': return COLOR_ORANGE;
        case 'DEAD': return COLOR_RED;
        default: return COLOR_GRAY;
    }
};

const getServerStatusChipColor = (status: ServerHealthStatus): 'success' | 'warning' | 'error' | 'default' => {
    switch (status) {
        case 'HEALTHY': return 'success';
        case 'UNDER_PRESSURE': return 'warning';
        case 'SICK': return 'warning';
        case 'DEAD': return 'error';
        default: return 'default';
    }
};

const getServerStatusIcon = (status: ServerHealthStatus) => {
    switch (status) {
        case 'HEALTHY': return <CheckCircleIcon sx={{ color: COLOR_GREEN }} fontSize="small" />;
        case 'UNDER_PRESSURE': return <WarningIcon sx={{ color: COLOR_YELLOW }} fontSize="small" />;
        case 'SICK': return <WarningIcon sx={{ color: COLOR_ORANGE }} fontSize="small" />;
        case 'DEAD': return <ErrorIcon sx={{ color: COLOR_RED }} fontSize="small" />;
        default: return <HelpOutlineIcon color="disabled" fontSize="small" />;
    }
};

const parseUsagePercent = (message: string): number | null => {
    const match = message.match(/(\d+(?:\.\d+)?)\s*%/);
    return match ? parseFloat(match[1]) : null;
};

const buildUsagePieData = (metric: ServerHealthMetricStatusResponse) => {
    const pct = parseUsagePercent(metric.message);
    const color = getServerStatusHex(metric.status);
    if (pct !== null) {
        return [
            { id: 0, value: pct, label: `Used ${pct}%`, color },
            { id: 1, value: 100 - pct, label: `Free ${(100 - pct).toFixed(1)}%`, color: COLOR_UNUSED_GRAY },
        ];
    }
    return [{ id: 0, value: 1, label: metric.status, color }];
};

const buildSmartPieData = (metric: ServerHealthMetricStatusResponse) => {
    if (metric.details.length === 0) {
        return [{ id: 0, value: 1, label: metric.status, color: getServerStatusHex(metric.status) }];
    }
    return metric.details.map((d, idx) => ({
        id: idx,
        value: 1,
        label: `${d.item} (${d.status})`,
        color: d.status === 'HEALTHY' ? COLOR_GREEN : getServerStatusHex(d.status),
    }));
};

// ─── Time filter options ─────────────────────────────────────────────────────

const TIME_FILTER_OPTIONS: { value: ServerHealthChartTimeFilter; label: string }[] = [
    { value: 'Last30Minutes', label: 'Last 30 min' },
    { value: 'Last60Minutes', label: 'Last 60 min' },
    { value: 'Last4Hours', label: 'Last 4 hours' },
    { value: 'Last12Hours', label: 'Last 12 hours' },
    { value: 'Last24Hours', label: 'Last 24 hours' },
];

// ─── Detail row component ────────────────────────────────────────────────────

const DetailRow: React.FC<{ label: string; status: ServerHealthStatus; message: string }> = ({ label, status, message }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {getServerStatusIcon(status)}
            <Typography variant="body2">{label}</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="caption" color="text.secondary">{message}</Typography>
            <Chip label={status} color={getServerStatusChipColor(status)} size="small" variant="outlined" />
        </Box>
    </Box>
);

// ─── Main Page ───────────────────────────────────────────────────────────────

const ServerHealthPage: React.FC = () => {
    const { serverId } = useParams<{ serverId: string }>();

    const [server, setServer] = useState<ServerHealthStatusResponseDto | null>(null);
    const [chartData, setChartData] = useState<ServerHealthChartResponseDto | null>(null);
    const [timeFilter, setTimeFilter] = useState<ServerHealthChartTimeFilter>('Last60Minutes');

    const [loading, setLoading] = useState(true);
    const [chartLoading, setChartLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const loadServerData = useCallback(async () => {
        if (!serverId) return;
        try {
            setLoading(true);
            setError(null);
            const servers = await getServersHealth();
            const found = servers.find(s => s.serverId === serverId);
            if (found) {
                setServer(found);
            } else {
                setError(`Server "${serverId}" not found.`);
            }
        } catch (err) {
            console.error('Error loading server data:', err);
            setError('Failed to load server health data.');
        } finally {
            setLoading(false);
        }
    }, [serverId]);

    const loadChartData = useCallback(async () => {
        if (!serverId) return;
        try {
            setChartLoading(true);
            const data = await getServerHealthCharts(serverId, timeFilter);
            setChartData(data);
        } catch (err) {
            console.error('Error loading chart data:', err);
        } finally {
            setChartLoading(false);
        }
    }, [serverId, timeFilter]);

    useEffect(() => {
        loadServerData();
    }, [loadServerData]);

    useEffect(() => {
        loadChartData();
    }, [loadChartData]);

    const handleTimeFilterChange = (event: SelectChangeEvent) => {
        setTimeFilter(event.target.value as ServerHealthChartTimeFilter);
    };

    const handleRefresh = () => {
        loadServerData();
        loadChartData();
    };

    // ─── Chart data preparation ──────────────────────────────────────────

    const cpuChartDates = chartData?.cpuUsageChart.map(r => new Date(r.date)) ?? [];
    const cpuChartValues = chartData?.cpuUsageChart.map(r => r.value) ?? [];
    const ramChartDates = chartData?.ramUsageChart.map(r => new Date(r.date)) ?? [];
    const ramChartValues = chartData?.ramUsageChart.map(r => r.value) ?? [];

    // ─── Loading state ───────────────────────────────────────────────────

    if (loading) {
        return (
            <Container maxWidth="xl" sx={{ py: 4, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
                <CircularProgress />
            </Container>
        );
    }

    if (error || !server) {
        return (
            <Container maxWidth="xl" sx={{ py: 4 }}>
                <Breadcrumbs sx={{ mb: 2 }}>
                    <MuiLink component={RouterLink} to="/health" underline="hover" color="inherit">Health</MuiLink>
                    <Typography color="text.primary">{serverId ?? 'Unknown'}</Typography>
                </Breadcrumbs>
                <Alert severity="error">{error ?? 'Server not found.'}</Alert>
                <Button component={RouterLink} to="/health" startIcon={<ArrowBackIcon />} sx={{ mt: 2 }}>
                    Back to Health Dashboard
                </Button>
            </Container>
        );
    }

    // ─── Time filter selector ────────────────────────────────────────────

    const TimeFilterSelector = (
        <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Time range</InputLabel>
            <Select value={timeFilter} label="Time range" onChange={handleTimeFilterChange}>
                {TIME_FILTER_OPTIONS.map(opt => (
                    <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                ))}
            </Select>
        </FormControl>
    );

    return (
        <Container maxWidth="xl" sx={{ py: 4 }}>
            {/* Breadcrumbs */}
            <Breadcrumbs sx={{ mb: 2 }}>
                <MuiLink component={RouterLink} to="/health" underline="hover" color="inherit">Health</MuiLink>
                <Typography color="text.primary">{server.serverId}</Typography>
            </Breadcrumbs>

            {/* Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <DnsIcon color="primary" sx={{ fontSize: 32 }} />
                    <Typography variant="h4" fontWeight="bold">{server.serverId}</Typography>
                    <Chip label={server.status} color={getServerStatusChipColor(server.status)} />
                </Box>
                <Button variant="outlined" startIcon={<RefreshIcon />} onClick={handleRefresh}>
                    Refresh
                </Button>
            </Box>

            {server.lastReportedAt && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    Last reported: {new Date(server.lastReportedAt).toLocaleString()}
                </Typography>
            )}

            {/* ═══════════════ CPU Usage Block ═══════════════ */}
            <Card variant="outlined" sx={{ mb: 3 }}>
                <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {getServerStatusIcon(server.cpu.status)}
                            <Typography variant="h6" fontWeight="bold">CPU Usage</Typography>
                            <Chip label={server.cpu.status} color={getServerStatusChipColor(server.cpu.status)} size="small" />
                        </Box>
                    </Box>

                    <Grid container spacing={3}>
                        {/* Pie chart */}
                        <Grid item xs={12} md={3}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                <PieChart
                                    series={[{
                                        data: buildUsagePieData(server.cpu),
                                        innerRadius: 30,
                                        outerRadius: 70,
                                        paddingAngle: 1,
                                        cornerRadius: 3,
                                    }]}
                                    height={180}
                                    width={220}
                                    margin={{ top: 5, bottom: 5, left: 5, right: 5 }}
                                    slotProps={{ legend: { hidden: true } }}
                                />
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                    {server.cpu.message}
                                </Typography>
                            </Box>
                        </Grid>

                        {/* Line chart */}
                        <Grid item xs={12} md={9}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                <Typography variant="subtitle2" color="text.secondary">CPU Usage Over Time (%)</Typography>
                                {TimeFilterSelector}
                            </Box>
                            {chartLoading ? (
                                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                                    <CircularProgress size={32} />
                                </Box>
                            ) : cpuChartDates.length > 0 ? (
                                <LineChart
                                    xAxis={[{ scaleType: 'time', data: cpuChartDates }]}
                                    yAxis={[{ min: 0, max: 100 }]}
                                    series={[{
                                        data: cpuChartValues,
                                        color: getServerStatusHex(server.cpu.status),
                                        area: true,
                                    }]}
                                    height={250}
                                />
                            ) : (
                                <Alert severity="info" sx={{ mt: 1 }}>No CPU chart data available for the selected time range.</Alert>
                            )}
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>

            {/* ═══════════════ RAM Usage Block ═══════════════ */}
            <Card variant="outlined" sx={{ mb: 3 }}>
                <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {getServerStatusIcon(server.ram.status)}
                            <Typography variant="h6" fontWeight="bold">RAM Usage</Typography>
                            <Chip label={server.ram.status} color={getServerStatusChipColor(server.ram.status)} size="small" />
                        </Box>
                    </Box>

                    <Grid container spacing={3}>
                        {/* Pie chart */}
                        <Grid item xs={12} md={3}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                <PieChart
                                    series={[{
                                        data: buildUsagePieData(server.ram),
                                        innerRadius: 30,
                                        outerRadius: 70,
                                        paddingAngle: 1,
                                        cornerRadius: 3,
                                    }]}
                                    height={180}
                                    width={220}
                                    margin={{ top: 5, bottom: 5, left: 5, right: 5 }}
                                    slotProps={{ legend: { hidden: true } }}
                                />
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                    {server.ram.message}
                                </Typography>
                            </Box>
                        </Grid>

                        {/* Line chart */}
                        <Grid item xs={12} md={9}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                <Typography variant="subtitle2" color="text.secondary">RAM Usage Over Time (%)</Typography>
                                {TimeFilterSelector}
                            </Box>
                            {chartLoading ? (
                                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                                    <CircularProgress size={32} />
                                </Box>
                            ) : ramChartDates.length > 0 ? (
                                <LineChart
                                    xAxis={[{ scaleType: 'time', data: ramChartDates }]}
                                    yAxis={[{ min: 0, max: 100 }]}
                                    series={[{
                                        data: ramChartValues,
                                        color: getServerStatusHex(server.ram.status),
                                        area: true,
                                    }]}
                                    height={250}
                                />
                            ) : (
                                <Alert severity="info" sx={{ mt: 1 }}>No RAM chart data available for the selected time range.</Alert>
                            )}
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>

            {/* ═══════════════ Disk Usage & S.M.A.R.T. Block ═══════════════ */}
            <Card variant="outlined" sx={{ mb: 3 }}>
                <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                        {getServerStatusIcon(server.space.status)}
                        <Typography variant="h6" fontWeight="bold">Disk Usage</Typography>
                        <Chip label={server.space.status} color={getServerStatusChipColor(server.space.status)} size="small" />
                    </Box>

                    <Grid container spacing={3}>
                        {/* Disk usage pie */}
                        <Grid item xs={12} md={4}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>Space Usage</Typography>
                                <PieChart
                                    series={[{
                                        data: buildUsagePieData(server.space),
                                        innerRadius: 30,
                                        outerRadius: 70,
                                        paddingAngle: 1,
                                        cornerRadius: 3,
                                    }]}
                                    height={180}
                                    width={220}
                                    margin={{ top: 5, bottom: 5, left: 5, right: 5 }}
                                    slotProps={{ legend: { hidden: true } }}
                                />
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                    {server.space.message}
                                </Typography>
                            </Box>
                        </Grid>

                        {/* Disk space details */}
                        <Grid item xs={12} md={4}>
                            {server.space.details.length > 0 && (
                                <Box>
                                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>Disk Partitions</Typography>
                                    {server.space.details.map((detail, idx) => (
                                        <DetailRow key={idx} label={detail.item} status={detail.status} message={detail.message} />
                                    ))}
                                </Box>
                            )}
                        </Grid>

                        {/* S.M.A.R.T. status */}
                        <Grid item xs={12} md={4}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                    <Typography variant="subtitle2" color="text.secondary">S.M.A.R.T. Health</Typography>
                                    <Chip label={server.smart.status} color={getServerStatusChipColor(server.smart.status)} size="small" variant="outlined" />
                                </Box>
                                <PieChart
                                    series={[{
                                        data: buildSmartPieData(server.smart),
                                        innerRadius: 30,
                                        outerRadius: 70,
                                        paddingAngle: 1,
                                        cornerRadius: 3,
                                    }]}
                                    height={180}
                                    width={220}
                                    margin={{ top: 5, bottom: 5, left: 5, right: 5 }}
                                    slotProps={{ legend: { hidden: true } }}
                                />
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                    {server.smart.message}
                                </Typography>
                                {server.smart.details.length > 0 && (
                                    <Box sx={{ mt: 1, width: '100%' }}>
                                        {server.smart.details.map((detail, idx) => (
                                            <DetailRow key={idx} label={detail.item} status={detail.status} message={detail.message} />
                                        ))}
                                    </Box>
                                )}
                            </Box>
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>
        </Container>
    );
};

export default ServerHealthPage;
